import { useCallback, useRef, useState } from "react";
import { useFolhaPagamentoPagination } from "../hooks/FolhaPagamento_lista/pagination";
import { FolhaPagamento } from "../../domain/models/folha_pagamento";
import { useFolhaPagamentoFilters } from "../hooks/FolhaPagamento_lista/filter";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FolhaPagamentoForm, FolhaPagamentoSchema } from "../schemas/FolhaPagamento_lista/folha_pagamento";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { CopiaFolhaPagamentoForm, CopiaFolhaPagamentoSchema } from "../schemas/FolhaPagamento_lista/copia_folha_pagamento";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { ReportFolhaPagamentoForm, initialFolhaPagamentoFormValues, ReportFolhaPagamentoSchema } from "../schemas/FolhaPagamento_lista/report_folha_pagamento";
import { ReportFolhaPagamentoDetalhadaForm } from "../schemas/FolhaPagamento_lista/report_folha_pagamento_detalhada";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useCachedDataLoader } from "../../../../common/hooks/use_cached_data_loader";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";

import CommonConstants from "../../../../common/constants";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";

/**
 * LoadFolhaPagamentoState
 */

type LoadFolhaPagamentoState = {
  tipoPesquisa?: number,
  valorPesquisa?: string | null,
  dataCadastro?: Date | null,
  dataPagamento?: Date | null,
  numero?: string | null,
  idFuncionario?: number | null,
  page?: number;
  rowsPerPage?: number;
};

/**
 * useFolhaPagamentoListaViewModel
 */

export const useFolhaPagamentoListaViewModel = () => {
  const {
    commons: {
      getFuncionariosDropDownUseCase,
      getCentroCustoDropDownUseCase,
      getCargosDropDownUseCase,
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    },
    reports: {
      getReportFolhaPagamentoUseCase,
      getReportFolhaPagamentoDetalhadaUseCase,
    },
    rh: {
      getSubcontasDropDownUseCase,
      getFolhaPagamentoDropDownUseCase,
      getFolhaPagamentoDetalhadaDropDownUseCase,
      getBancoPagamentoDropDownUseCase,
      getAllFolhaPagamentoUseCase,
      createFolhaPagamentoUseCase,
      updateFolhaPagamentoUseCase,
      deleteFolhaPagamentoUseCase,
      copyFolhaPagamentoUseCase,
      getFiltroFolhaPagamentoUseCase,
      createFiltroFolhaPagamentoUseCase,
    },
  } = useDependencyInjection();

  const debounceHandler = useCreateDebounceHandler(750);

  const menuRef = useRef<HTMLButtonElement>(null);

  const { idUsuario } = useUserAuthStore();

  const [openMenu, setOpenMenu] = useState(false);

  const { setPageSaving, pushErrorNotification, pushInfoNotification } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [savedColunas, setSavedColunas] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [openCadastroFolhaPagamentoModal, setOpenCadastroFolhaPagamentoModal] = useState(false);
  const [openCopiaFolhaPagamentoModal, setOpenCopiaFolhaPagamentoModal] = useState(false);
  const [openDeleteFolhaPagamentoModal, setOpenDeleteFolhaPagamentoModal] = useState(false);

  const [openReportFolhaPagamento, setOpenReportFolhaPagamento] = useState(false);
  const [openReportFolhaPagamentoDetalhada, setOpenReportFolhaPagamentoDetalhada] = useState(false);

  const [selectedFolhaPagamento, setSelectedFolhaPagamento] = useState<FolhaPagamento>();

  const [folhaPagamentos, setFolhaPagamentos] = useState<FolhaPagamento[]>();

  const hookForm = useForm<FolhaPagamentoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(FolhaPagamentoSchema),
  });

  const hookFormCopia = useForm<CopiaFolhaPagamentoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(CopiaFolhaPagamentoSchema),
  });

  const hookFormReportFolhaPagamento = useForm<ReportFolhaPagamentoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: initialFolhaPagamentoFormValues,
    resolver: zodResolver(ReportFolhaPagamentoSchema),
  });

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: (error) => pushErrorNotification("Erro", [error]),
  });

  const filters = useFolhaPagamentoFilters();
  const pagination = useFolhaPagamentoPagination();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  /**
   * Funções de carregamento
   */

  const loadFuncionariosDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.funcionariosDropDown,
    loader: async () => await getFuncionariosDropDownUseCase.getFuncionariosDropDown(undefined, true),
    onSet: dt => useFolhaPagamentoFilters.setState({ funcionarios: dt }),
    onError: e => {
      useFolhaPagamentoFilters.setState({ funcionarios: [] });
      throw e;
    }
  });


  const loadCentroCustoDropDown = useCachedDataLoader({
    key: CommonConstants.cacheKey.centroCustoDropDown,
    loader: async () => await getCentroCustoDropDownUseCase.getCentroCustoDropDown(),
    onSet: dt => useFolhaPagamentoFilters.setState({ centroCustos: dt }),
    onError: e => {
      useFolhaPagamentoFilters.setState({ centroCustos: [] });
      throw e;
    }
  });

  const loadSubcontasDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.subContasDropDown,
    loader: async () => await getSubcontasDropDownUseCase.getSubcontasDropDown(),
    onSet: dt => useFolhaPagamentoFilters.setState({ subcontas: dt }),
    onError: e => {
      useFolhaPagamentoFilters.setState({ subcontas: [] });
      throw e;
    }
  });

  const loadFolhaPagamentoDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.folhaPagamentoDropDown,
    loader: async () => await getFolhaPagamentoDropDownUseCase.getFolhaPagamentoDropDown(),
    onSet: dt => useFolhaPagamentoFilters.setState({ folhaPagamentos: dt }),
    onError: e => {
      useFolhaPagamentoFilters.setState({ folhaPagamentos: [] });
      throw e;
    }
  });

  const loadFolhaPagamentoDetalhadasDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.folhaPagamentoDetalhasDropDown,
    loader: async () => await getFolhaPagamentoDetalhadaDropDownUseCase.getFolhaPagamentoDetalhadaDropDown(),
    onSet: dt => useFolhaPagamentoFilters.setState({ folhaPagamentoDetalhadas: dt }),
    onError: e => {
      useFolhaPagamentoFilters.setState({ folhaPagamentoDetalhadas: [] });
      throw e;
    }
  });

  const loadBancoPagamentoDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.bancoPagamentoDropDown,
    loader: async () => await getBancoPagamentoDropDownUseCase.getBancoPagamentoDropDown(),
    onSet: dt => useFolhaPagamentoFilters.setState({ bancoPagamentos: dt }),
    onError: e => {
      useFolhaPagamentoFilters.setState({ bancoPagamentos: [] });
      throw e;
    }
  });

  const loadCargosDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.cargoDropDown,
    loader: async () => await getCargosDropDownUseCase.getCargosDropDown(),
    onSet: dt => useFolhaPagamentoFilters.setState({ cargos: dt }),
    onError: e => {
      useFolhaPagamentoFilters.setState({ cargos: [] });
      throw e;
    }
  });

  const loadFolhaPagamentos = useCallback(async (funcState?: LoadFolhaPagamentoState) => {
    try {
      const filteredTipoPesquisa = funcState?.tipoPesquisa ?? filters.tipoPesquisa;
      const filteredValorPesquisa = evaluateParamValue(funcState?.valorPesquisa, filters.valorPesquisa);
      const filteredIdFuncionario = evaluateParamValue(funcState?.idFuncionario, filters.idFuncionario);
      const filteredDataCadastro = evaluateParamValue(funcState?.dataCadastro, filters.dataCadastro);
      const filteredDataPagamento = evaluateParamValue(funcState?.dataPagamento, filters.dataPagamento);
      const filteredNumero = evaluateParamValue(funcState?.numero, filters.numero);

      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllFolhaPagamentoUseCase.getAllFolhaPagamento(
        filteredTipoPesquisa,
        filteredValorPesquisa,
        filteredIdFuncionario,
        filteredDataCadastro,
        filteredDataPagamento,
        filteredNumero,
        filteredPage,
        filteredRowsPerPage,
      );

      setFolhaPagamentos(getAllFolhaPagamentoUseCase.items);

      pagination.setTotalItems(getAllFolhaPagamentoUseCase.totalItems);
    } catch (e: unknown) {
      setFolhaPagamentos([]);
      throw e;
    }
  }, [
    filters,
    pagination,
    getAllFolhaPagamentoUseCase,
  ]);

  const loadFiltroFolhaPagamento = useCallback(async () => {
    try {
      const filtroFolhaPagamento = await getFiltroFolhaPagamentoUseCase.getFiltroFolhaPagamento()
      useFolhaPagamentoFilters.setState({ filtroFolhaPagamento });
    } catch (e: unknown) {
      useFolhaPagamentoFilters.setState({ filtroFolhaPagamento: undefined });
      throw e;
    }
  }, [
    getFiltroFolhaPagamentoUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadFuncionariosDropdown();
      await loadSubcontasDropdown();
      await loadCentroCustoDropDown();
      await loadFolhaPagamentoDropdown();
      await loadFolhaPagamentoDetalhadasDropdown();
      await loadBancoPagamentoDropdown();
      await loadCargosDropdown();
      await loadFiltroFolhaPagamento();
      await loadFolhaPagamentos();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao iniciar pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleFolhaPagamentoSubmit = useCallback(async (form: FolhaPagamentoForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (selectedFolhaPagamento) {
        await updateFolhaPagamentoUseCase.updateFolhaPagamento(
          selectedFolhaPagamento.idFolhaPagamento,
          form.dataCadastro,
          form.dataPagamento,
          form.descricao,
          form.tipo,
        );
      } else {
        await createFolhaPagamentoUseCase.createFolhaPagamento(
          form.dataCadastro,
          form.dataPagamento,
          form.descricao,
          form.tipo,
        );
      }
      await loadFolhaPagamentos();
      setOpenCadastroFolhaPagamentoModal(false);
      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao salvar folha de pagamento!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    selectedFolhaPagamento,
    createFolhaPagamentoUseCase,
    updateFolhaPagamentoUseCase,
    setPageSaving,
    pushErrorNotification,
    loadFolhaPagamentos,
    pushInfoNotification,
  ]);

  const handleCopiaFolhaPagamentoSubmit = useCallback(async (form: CopiaFolhaPagamentoForm) => {
    try {
      setLoading(true);
      if (selectedFolhaPagamento) {
        await copyFolhaPagamentoUseCase.copyFolhaPagamento(
          selectedFolhaPagamento.idFolhaPagamento,
          form.dataCadastro,
          form.dataPagamento,
          form.descricao,
        );
        await loadFolhaPagamentos();
        pushInfoNotification('Sucesso', 'Registro copiado com sucesso!');
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao copiar folha de pagamento!'));
    } finally {
      setLoading(false);
      setOpenCopiaFolhaPagamentoModal(false);
    }
  }, [
    selectedFolhaPagamento,
    copyFolhaPagamentoUseCase,
    pushErrorNotification,
    loadFolhaPagamentos,
    pushInfoNotification,
  ]);

  const handleCreateFiltroFolhaPagamentoSubmit = useCallback(async () => {
    try {
      setSaving(true);
      setSavedColunas(false);

      const colunasForm = hookFormReportFolhaPagamento.getValues('colunas');
      const colunas: string[] = [];

      if (colunasForm.nro) colunas.push('NR_CONTAS_PAGAR');
      if (colunasForm.centroCusto) colunas.push('CENTRO_CUSTO');
      if (colunasForm.folhaPagamento) colunas.push('FOLHA');
      if (colunasForm.funcionarioFolha) colunas.push('FUNCIONARIO_FOLHA');
      if (colunasForm.bancoPagamento) colunas.push('BANCO_PAGAMENTO');
      if (colunasForm.notaFiscal) colunas.push('NOTA_FISCAL');
      if (colunasForm.nroDoc) colunas.push('NRO_DOCUMENTO');
      if (colunasForm.responsavel) colunas.push('RESPONSAVEL');
      if (colunasForm.status) colunas.push('STATUS');
      if (colunasForm.titulo) colunas.push('TITULO');
      if (colunasForm.subConta) colunas.push('SUBCONTA');
      if (colunasForm.dataPagar) colunas.push('DATA_A_PAGAR');
      if (colunasForm.dataPago) colunas.push('DATA_PAGO');
      if (colunasForm.valorPagar) colunas.push('VALOR_A_PAGAR');
      if (colunasForm.valorPago) colunas.push('VALOR_PAGO');
      if (colunasForm.observacoes) colunas.push('OBSERVACOES');

      await createFiltroFolhaPagamentoUseCase.createFiltroFolhaPagamento(colunas);

      await loadFiltroFolhaPagamento();

      setSavedColunas(true);
      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar filtro de folha de pagamento!"));
    } finally {
      setSaving(false);
    }
  }, [
    hookFormReportFolhaPagamento,
    createFiltroFolhaPagamentoUseCase,
    loadFiltroFolhaPagamento,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadFuncionariosDropdown();
      await loadSubcontasDropdown();
      await loadCentroCustoDropDown();
      await loadFolhaPagamentoDropdown();
      await loadFolhaPagamentoDetalhadasDropdown();
      await loadBancoPagamentoDropdown();
      await loadCargosDropdown();
      await loadFiltroFolhaPagamento();
      await loadFolhaPagamentos();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao atualizar pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadFuncionariosDropdown,
    loadSubcontasDropdown,
    loadCentroCustoDropDown,
    loadFolhaPagamentoDropdown,
    loadFolhaPagamentoDetalhadasDropdown,
    loadBancoPagamentoDropdown,
    loadCargosDropdown,
    loadFiltroFolhaPagamento,
    loadFolhaPagamentos,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useFolhaPagamentoPagination.setState({ page: page + 1 });
      await loadFolhaPagamentos({ page: page + 1 })
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao atualizar pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadFolhaPagamentos,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useFolhaPagamentoPagination.setState({ page: 1, rowsPerPage });
      await loadFolhaPagamentos({ page: 1, rowsPerPage })
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao atualizar pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadFolhaPagamentos,
  ]);

  const handleTipoPesquisaChange = useDebouncedCallback(debounceHandler, (tipoPesquisa: number | null) => {
    useFolhaPagamentoFilters.setState({ tipoPesquisa: tipoPesquisa ?? undefined });
    return async () => {
      try {
        setLoading(true);
        useFolhaPagamentoPagination.setState({ page: 1 });
        await loadFolhaPagamentos({ tipoPesquisa: tipoPesquisa ?? 1, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao atualizar pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadFolhaPagamentos,
    pushErrorNotification,
  ]);

  const handleValorPesquisaChange = useDebouncedCallback(debounceHandler, (valorPesquisa: string | null) => {
    useFolhaPagamentoFilters.setState({ valorPesquisa });
    return async () => {
      try {
        setLoading(true);
        useFolhaPagamentoPagination.setState({ page: 1 });
        await loadFolhaPagamentos({ valorPesquisa, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao atualizar pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadFolhaPagamentos,
    pushErrorNotification,
  ]);

  const handleFuncionarioChange = useDebouncedCallback(debounceHandler, (idFuncionario: number | null) => {
    useFolhaPagamentoFilters.setState({ idFuncionario });
    return async () => {
      try {
        setLoading(true);
        useFolhaPagamentoPagination.setState({ page: 1 });
        await loadFolhaPagamentos({ idFuncionario, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao atualizar pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadFolhaPagamentos,
    pushErrorNotification,
  ]);

  const handleDataCadastroChange = useDebouncedCallback(debounceHandler, (dataCadastro: Date | null) => {
    useFolhaPagamentoFilters.setState({ dataCadastro });
    return async () => {
      if (dataCadastro === null || !isNaN(dataCadastro.getTime())) {
        try {
          setLoading(true);
          useFolhaPagamentoPagination.setState({ page: 1 });
          await loadFolhaPagamentos({ dataCadastro, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification('Erro', processError(e, 'Falha ao atualizar pagina!'));
        } finally {
          setLoading(false);
        }
      }
    };
  }, [
    loadFolhaPagamentos,
    pushErrorNotification,
  ]);

  const handleDataPagamentoChange = useDebouncedCallback(debounceHandler, (dataPagamento: Date | null) => {
    useFolhaPagamentoFilters.setState({ dataPagamento });
    return async () => {
      try {
        setLoading(true);
        useFolhaPagamentoPagination.setState({ page: 1 });
        await loadFolhaPagamentos({ dataPagamento, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao atualizar pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadFolhaPagamentos,
    pushErrorNotification,
  ]);

  const handleNumeroChange = useDebouncedCallback(debounceHandler, (numero: string | null) => {
    useFolhaPagamentoFilters.setState({ numero });
    return async () => {
      try {
        setLoading(true);
        useFolhaPagamentoPagination.setState({ page: 1 });
        await loadFolhaPagamentos({ numero, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao atualizar pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadFolhaPagamentos,
    pushErrorNotification,
  ]);

  const handleClear = useCallback(async () => {
    try {
      setLoading(true);
      useFolhaPagamentoFilters.getState().clear();
      useFolhaPagamentoPagination.setState({ page: 1 });
      await loadFolhaPagamentos({
        tipoPesquisa: 1,
        valorPesquisa: null,
        dataCadastro: null,
        dataPagamento: null,
        numero: null,
        idFuncionario: null,
        page: 1
      });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao atualizar pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadFolhaPagamentos,
    pushErrorNotification,
  ]);

  const handleOpenFolhaPagamentoModalOpenState = useCallback((idFolhaPagamento: number | null, openState: boolean) => {
    if (openState) {
      if (idFolhaPagamento && folhaPagamentos) {
        setSelectedFolhaPagamento(folhaPagamentos.find(x => x.idFolhaPagamento === idFolhaPagamento));
      } else {
        setSelectedFolhaPagamento(undefined);
      }
    }
    setOpenCadastroFolhaPagamentoModal(openState);
  }, [
    folhaPagamentos,
  ]);

  const handleOpenCopiaFolhaPagamentoModalOpenState = useCallback((idFolhaPagamento: number | null, openState: boolean) => {
    if (openState) {
      if (idFolhaPagamento && folhaPagamentos) {
        setSelectedFolhaPagamento(folhaPagamentos.find(x => x.idFolhaPagamento === idFolhaPagamento));
      } else {
        setSelectedFolhaPagamento(undefined);
      }
    }
    setOpenCopiaFolhaPagamentoModal(openState);
  }, [
    folhaPagamentos,
  ]);

  const handleViewFolhaPagamento = useCallback((idFolhaPagamento: number | null, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/FolhaPagamento_cadastro?id=${idFolhaPagamento}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/FolhaPagamento_cadastro?id=${idFolhaPagamento}`);
    }
  }, [
    backOrNavigate,
  ]);

  const handleOpenDeleteFolhaPagamentoModal = useCallback((idFolhaPagamento: number | null, openState: boolean) => {
    if (openState) {
      if (idFolhaPagamento && folhaPagamentos) {
        setSelectedFolhaPagamento(folhaPagamentos.find(x => x.idFolhaPagamento === idFolhaPagamento));
      } else {
        setSelectedFolhaPagamento(undefined);
      }
    }
    setOpenDeleteFolhaPagamentoModal(openState);
  }, [
    folhaPagamentos,
  ]);

  const handleConfirmarDeletarFolhaPagamento = useCallback(async () => {
    try {
      setSaving(true);
      if (selectedFolhaPagamento) {
        await deleteFolhaPagamentoUseCase.deleteFolhaPagamento(selectedFolhaPagamento.idFolhaPagamento);
        await loadFolhaPagamentos();
        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao atualizar pagina!'));
    } finally {
      setSaving(false);
      setOpenDeleteFolhaPagamentoModal(false);
    }
  }, [
    selectedFolhaPagamento,
    deleteFolhaPagamentoUseCase,
    pushErrorNotification,
    loadFolhaPagamentos,
    pushInfoNotification,
  ]);

  const handleOpenChangeHistory = useCallback(() => {
    if (selectedFolhaPagamento) {
      changeHistory.handleOpenChangeHistory(selectedFolhaPagamento.idFolhaPagamento,
        LogTipoTabela.FOLHA_PAGAMENTO, true);
    }
  }, [
    selectedFolhaPagamento,
    changeHistory,
  ]);

  const handleToggleMenu = useCallback((openState: boolean) => {
    setOpenMenu(openState);
  }, []);

  const handleListKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpenMenu(false);
    } else if (event.key === 'Escape') {
      setOpenMenu(false);
    }
  }, []);

  const handleReportFolhaPagamentoOpenState = useCallback((openState: boolean) => {
    setSavedColunas(false);
    setOpenMenu(false);
    setOpenReportFolhaPagamento(openState);
  }, [
  ]);

  const handleReportFolhaPagamentoDetalhadaOpenState = useCallback((openState: boolean) => {
    setOpenMenu(false);
    setOpenReportFolhaPagamentoDetalhada(openState);
  }, [
  ]);

  const handleSubmitReportFolhaPagamento = useCallback(async (form: ReportFolhaPagamentoForm) => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportFolhaPagamentoUseCase.getReportFolhaPagamento(
            requiredMimeType,
            form.titulo,
            form.subcontas,
            form.folhaPagamentos,
            form.centroCustos,
            form.funcionarios,
            form.bancoPagamentos,
            form.status,
            form.dataPagarDe,
            form.dataPagarAte,
            form.dataPagoDe,
            form.dataPagoAte,
            form.colunas,
          ),
        closeReportModalCallback: () => {
          setOpenReportFolhaPagamento(false);
        },
      });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao atualizar pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    getReportFolhaPagamentoUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleSubmitReportFolhaPagamentoDetalhada = useCallback(async (form: ReportFolhaPagamentoDetalhadaForm) => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportFolhaPagamentoDetalhadaUseCase.getReportFolhaPagamentoDetalhada(
            requiredMimeType,
            idUsuario,
            form.folhaPagamentos,
            form.centroCustos,
            form.funcionarios,
            form.cargos,
            form.dataCadastroDe,
            form.dataCadastroAte,
            form.dataPagamentoDe,
            form.dataPagamentoAte,
          ),
        closeReportModalCallback: () => {
          setOpenReportFolhaPagamentoDetalhada(false);
        },
      });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao atualizar pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    idUsuario,
    getReportFolhaPagamentoDetalhadaUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  return {
    menuRef,
    openMenu,
    loading,
    saving,
    savedColunas,
    errorMessage,
    openCadastroFolhaPagamentoModal,
    openCopiaFolhaPagamentoModal,
    openDeleteFolhaPagamentoModal,
    openReportFolhaPagamento,
    openReportFolhaPagamentoDetalhada,
    selectedFolhaPagamento,
    folhaPagamentos: folhaPagamentos ?? [],
    hookForm,
    hookFormCopia,
    hookFormReportFolhaPagamento,
    changeHistory,
    filters,
    pagination,
    reportViewerState,
    handleFolhaPagamentoSubmit,
    handleCopiaFolhaPagamentoSubmit,
    handleCreateFiltroFolhaPagamentoSubmit,
    handleReload,
    handlePageChange,
    handleRowsPerPageChange,
    handleTipoPesquisaChange,
    handleValorPesquisaChange,
    handleFuncionarioChange,
    handleDataCadastroChange,
    handleDataPagamentoChange,
    handleNumeroChange,
    handleClear,
    handleOpenFolhaPagamentoModalOpenState,
    handleOpenCopiaFolhaPagamentoModalOpenState,
    handleViewFolhaPagamento,
    handleOpenDeleteFolhaPagamentoModal,
    handleConfirmarDeletarFolhaPagamento,
    handleOpenChangeHistory,
    handleToggleMenu,
    handleListKeyDown,
    handleReportFolhaPagamentoOpenState,
    handleReportFolhaPagamentoDetalhadaOpenState,
    handleSubmitReportFolhaPagamento,
    handleSubmitReportFolhaPagamentoDetalhada,
  };
}